import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import Modal from "../../components/Modal"
import { useRequest } from "../../hooks/useRequest"
import { userStore } from "../../store/userStore"
import { appStore } from "../../store/appStore"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type PromoStatus = "idle" | "loading" | "success" | "error"

type PromoCodeSheetProps = {
    onClose: () => void
}

/* Шторка ввода промокода поверх шага оплаты (тот же API, что и в профиле) */
export default function PromoCodeSheet({ onClose }: PromoCodeSheetProps) {
    const { requestData, request } = useRequest()
    const { user, setUser } = userStore()
    const { city } = appStore()

    const [code, setCode] = useState("")
    const [status, setStatus] = useState<PromoStatus>("idle")
    const [message, setMessage] = useState("")

    const card = user?.cards.find(c => !c.blocked) ?? user?.cards?.[0] ?? null

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.toUpperCase()
        if (/^[A-Z0-9]*$/.test(val)) {
            setCode(val)
            if (status !== "idle") setStatus("idle")
            setMessage("")
        }
    }

    const submit = async () => {
        if (!card || !code.trim() || status === "loading") return
        setStatus("loading")
        setMessage("")

        const res = await requestData({
            type: "POST",
            path: `user/card/${card.id}/promo`,
            sendData: { city_id: city.id, code },
            disableError: true,
        })

        if (res.status === "success") {
            setStatus("success")
            setMessage((res.data as { message?: string } | null)?.message ?? "Промокод активирован — бонусы уже на карте")
            request("GET", `user?city_id=${city.id}`)
                .then(data => setUser(data))
                .catch(() => {})
        } else {
            setStatus("error")
            setMessage((res as any).error?.message ?? "Промокод не найден или уже использован")
        }
    }

    const disabled = !code.trim() || status === "loading"

    return (
        <Modal sheet compact onDismiss={onClose}>
            <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] px-5 pb-[max(1.5rem,var(--safe-bottom))] pt-2 sm:pb-8">
                <h3
                    className="text-white"
                    style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 20, letterSpacing: "-0.01em" }}>
                    🎟 Промокод
                </h3>

                {!card ? (
                    <p className="text-white/50 text-[14px]" style={{ fontFamily: BODY }}>
                        Для активации промокода нужна карта лояльности Мягкого кинотеатра
                    </p>
                ) : (
                    <>
                        <p className="text-white/55 text-[13px] leading-relaxed" style={{ fontFamily: BODY }}>
                            Введи промокод — бонусы начислятся на карту, и их сразу можно списать
                        </p>

                        <input
                            type="text"
                            value={code}
                            onChange={handleChange}
                            onKeyDown={e => e.key === "Enter" && !disabled && submit()}
                            placeholder="ПРОМОКОД"
                            maxLength={32}
                            className="w-full border-b-2 bg-transparent py-3 text-center text-[24px] text-white outline-none transition-colors placeholder:text-white/15 focus:border-white/40"
                            style={{
                                fontFamily: MONO,
                                fontWeight: 800,
                                letterSpacing: "0.12em",
                                borderColor:
                                    status === "error"
                                        ? "rgba(228,32,107,0.6)"
                                        : status === "success"
                                          ? "rgba(41,204,106,0.6)"
                                          : "rgba(255,255,255,0.15)",
                            }}
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                        />

                        <motion.button
                            whileHover={status === "success" || !disabled ? { scale: 1.02 } : {}}
                            whileTap={status === "success" || !disabled ? { scale: 0.97 } : {}}
                            onClick={status === "success" ? onClose : submit}
                            disabled={status !== "success" && disabled}
                            className="w-full rounded-[14px] py-3.5 text-[14px] transition-all"
                            style={{
                                fontFamily: MONO,
                                fontWeight: 700,
                                letterSpacing: "0.05em",
                                background:
                                    status === "success"
                                        ? "#29cc6a"
                                        : disabled
                                          ? "rgba(255,255,255,0.06)"
                                          : "#349be6",
                                color: status === "success" || !disabled ? "#fff" : "rgba(255,255,255,0.3)",
                                boxShadow:
                                    status === "success"
                                        ? "0 0 24px rgba(41,204,106,0.4)"
                                        : disabled
                                          ? "none"
                                          : "0 0 24px rgba(52,155,230,0.4)",
                            }}>
                            {status === "loading" ? "Проверяем..." : status === "success" ? "Готово" : "Активировать"}
                        </motion.button>

                        <AnimatePresence mode="wait">
                            {(status === "success" || status === "error") && (
                                <motion.div
                                    key={status}
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="flex items-center gap-2 rounded-[12px] px-4 py-3"
                                    style={
                                        status === "success"
                                            ? { background: "rgba(41,204,106,0.12)", border: "1px solid rgba(41,204,106,0.3)" }
                                            : { background: "rgba(228,32,107,0.1)", border: "1px solid rgba(228,32,107,0.3)" }
                                    }>
                                    <span className="text-[16px]">{status === "success" ? "✅" : "❌"}</span>
                                    <p
                                        className="text-[13px]"
                                        style={{ fontFamily: BODY, color: status === "success" ? "#29cc6a" : "#e4206b" }}>
                                        {message}
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </div>
        </Modal>
    )
}
